import { InterfaceFactoryOptions } from './factory-options.interface';
import { Container } from '@tencent/alloy-renderer';
import { RenderPostionType } from '@src/js/constant';
import { PlaceHolderComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/place-holder-component';
import { ElementBaseComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/element-base-component';
import { TextEventRectComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-event-rect-component';
import { ShapeLinkFlagComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/shape-link-flag-component';
import {
    getHasCustomPrompt,
    getIsCustomPlaceholder,
    getPlaceHolderUnsupport,
    getSize,
    isPlaceHolderShow,
} from '@editor/src/page/index/components/canvas/element-types/shape-element/utils';
import { getTextRect } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-rect';
import { IEditorProps } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import { reverseComponentFlip } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/reverse-component-flip';
import { PlaceholderElement } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/place-holder-component/placeholder-element';
import { isEditorEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { ForegroundComponent } from '../components/foreground-component';
import { isTitleElement } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils';
import { ITextRect } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-common.interface';
import { CANVAS_SHAPE_ID_PREFIX } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import { savePlaceHolderParaEndProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/place-holder-component/utils';
import { checkClickElement } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-pointer-cursor';
import { isCopyToImageOverSize } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/copy-to-image/over-size-control';
import { getSlideIndex } from '@src/slide/domain/ui-model/element-modeles/property/slide-number/operation';
import { is3DModel, isIframeShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { AddonManagerEventsKeys, IAddonManager } from '@src/slide/application/service/addon/addon-manager.interface';
import addonDefaultForeground from '@src/img/default-iframe.svg';
import { AddonDoNotAddIconList, IframeIconComponent } from '../components/player-icon-component/iframe-icon-component';
import type { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import RendererComponent3D from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/3d';
import { shouldRender3D } from './utils/toggle-3d';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import isEmpty from 'lodash/isEmpty';
import { FORMAT_BRUSH_TEXT } from '@src/slide/application/service/format-brush/core/constant';
import { getBrushCursorStyle, needBrushCursor } from '@src/slide/application/service/format-brush/core/util';
import { ModelMenuRefreshEventsKeys } from '@src/slide/domain/ui-model/element-modeles/Interface/model-menu-refresh.interface';
import { setTextRunStyles } from '@src/slide/domain/ui-model/element-modeles/property/text/text-operation';
import { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { IAnimOption } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import {
    ITextBodyStyle,
    ITextRunStyle,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { TextEditor } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor';
import { WordArtComponent, checkShouldRenderWordArt } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/word-art-component';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { updateContainerTransform } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/utils/update-wrapper-transform';
import { getPointsAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import { FORBID_WEBGL_FILTER } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
import { CANVAS_PT_SCALE } from '@src/slide/infrastructure/module/independent/unit';
import { isShowTextFill } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/link';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IReactionDisposer, reaction } from 'mobx';

/**
 * 普通图形模块
 */
export class RendererShapeComponent extends Container {
    // 是否显示临时的 textEditor
    public showTempTextEditor = false;
    // 图形构造入参
    public factoryOptions: InterfaceFactoryOptions<IShapeModel>;
    // 基础图形
    public baseComponent: ElementBaseComponent;
    public baseComponentEvent3D?: ElementBaseComponent;
    // 前景图（目前 iframe 使用）
    public foregroundComponent: ForegroundComponent | null;
    // 播放按钮小图标（目前 iframe 使用）
    public iframeIconComponent: IframeIconComponent | null;
    // 链接标识小图标
    public shapeLinkFlagComponent: ShapeLinkFlagComponent;
    // 母版占位符
    public placeHolderComponent: PlaceHolderComponent | null;
    // 文本框
    public textEditor: TextEditor | null;
    // 文本响应区域
    private textEventRectComponent: TextEventRectComponent;
    // 文本容器，如果有单独的 textFrame，就需要用这个容器还原 textFrame
    private textContainer: Container | null;
    // 3d 场景的 textEditor 实例
    private editor3D?: TextEditor | null;
    // 文本框的占位符边框
    private placeHolderGraphics: PlaceholderElement | null;
    // 外侧控制是否渲染 3d 的开关
    private shouldRender3D = true;
    // 3d 渲染组件
    private rendererComponent3D?: RendererComponent3D | null;
    // 艺术字
    private wordArtComponent: WordArtComponent | null;
    // animBrushManager disposer
    private animBrushDisposer?: IReactionDisposer;

    public constructor(factoryOptions: InterfaceFactoryOptions<IShapeModel>) {
        super();
        this.factoryOptions = factoryOptions;
        const { model } = this.factoryOptions;
        // 图形渲染
        if (this.isShouldRender()) {
            this.renderAllGraphic();
        }
        this.id = `${CANVAS_SHAPE_ID_PREFIX}-${model.key}`;
        if (isIframeShapeModel(model)) {
            const addonManager = this.getAddonManager();
            addonManager?.event.on(AddonManagerEventsKeys.ADDON_INFO_LOAD_READY, this.handleAddonInfoListLoaded);
        }
    }

    public updateComponent = (): void => {
        if (this.isShouldRender()) {
            this.renderAllGraphic();
            this.componentDidUpdate();
        }
        this.observeAnimBrushManager();
    };

    public dispose(): void {
        // 移除model中editor组件
        super.dispose();
        this.logic?.dispose?.();
        if (!this.factoryOptions.fromScale) {
            // 和注册逻辑对应
            this.factoryOptions.model.setEditorComponent(null);
        }
        const addonManager = this.getAddonManager();
        addonManager?.event.off(AddonManagerEventsKeys.ADDON_INFO_LOAD_READY, this.handleAddonInfoListLoaded);
        this.dispose3D();
        this.animBrushDisposer?.();
    }

    public renderEditor = (): void => {
        // 母版占位符
        this.renderPlaceHolder();
        // 文本容器
        this.renderTextContainer();
        // 文本框
        this.renderTextEditor();
    };

    /**
     * 渲染临时的 TextEditor
     * 占位符被选中时可以直接输入，所以就渲染一个临时的 TextEditor
     */
    public renderTempEditor(): void {
        this.showTempTextEditor = true;
        this.renderEditor();
    }

    /**
     * 移除临时的 TextEditor
     */
    public clearTempEditor(): void {
        this.showTempTextEditor = false;
        this.renderEditor();
    }

    public updateTextEditorCursor(cursor: string): void {
        this.textEditor?.updateCursor(cursor);
    }

    /**
     * 判断当前是否渲染了占位符组件
     */
    public get isRenderPlaceholderComponent(): boolean {
        return !!this.placeHolderComponent;
    }

    /**
     * 当前 placeholder 是否可编辑文字
     */
    public get isPlaceholderTextEditable(): boolean {
        return this.placeHolderComponent?.isTextEditable?.() || false;
    }

    /**
     * 控制是否本组件要渲染 3d
     * @param shouldRender3D
     */
    public setShouldRender3D(shouldRender3D: boolean): void {
        this.shouldRender3D = shouldRender3D;
    }

    public is3DShape(): boolean {
        const { model } = this.factoryOptions as { model: IShapeModel };
        return !isEmpty(model.shape3d) || !isEmpty(model.scene3d);
    }

    private componentDidUpdate() {
        const { model, fromScale } = this.factoryOptions;
        if (!fromScale) {
            // 此方法本应该在ShapeModel的didConvertDataSdkToUi中调用，但是内部需要从editor取值，所以只能先放在这里了
            model.getMenuManager()?.event.emit(ModelMenuRefreshEventsKeys.REFRESH_MENU_ITEM_VALUES);
            model.getMenuManager()?.event.emit(ModelMenuRefreshEventsKeys.REFRESH_GROUP_ITEM_VALUES);
        }
    }

    private get logic(): any {
        return this.factoryOptions.logicService.getLogic(this.factoryOptions);
    }

    // 所有图形模块渲染
    private renderAllGraphic = () => {
        if (this.checkShouldRender3D()) {
            this.render3D();
            return;
        }

        // 基础图形部分
        this.renderBase();
        // todo: 旋转时，屏蔽inner部分、非图形的刷新，因为图片填充和渐变填充会响应旋转，等组合支持后添加
        // 文本响应区域
        this.renderTextEventRect();
        // 艺术字预渲染
        this.preRenderWordArt();
        // 文本相关
        this.renderEditor();
        // 艺术字
        this.renderWordArt();
        // 链接标识小图标（右键编辑链接生成）
        this.renderShapeLinkFlag();

        if (isIframeShapeModel(this.factoryOptions.model)) {
            this.renderForeground();
            this.renderPlayerIcon();
        } else {
            // 主要处理场景为 iframe 卡片态切换为文字态，移除前景图和小图标
            if (this.foregroundComponent) {
                this.removeChild(this.foregroundComponent);
                this.foregroundComponent.dispose();
                this.foregroundComponent = null;
            }

            if (this.iframeIconComponent) {
                this.iframeIconComponent.updateComponent();
            }
        }
    };

    private render3D = () => {
        if (!this.rendererComponent3D) {
            this.rendererComponent3D = new RendererComponent3D(this.factoryOptions, (textureCanvas) => {
                if (this.checkShouldRenderFlatText()) {
                    // 如果文字保持平面渲染，则不渲染上层文字纹理
                    return;
                }
                this.renderEditor();
                if (this.textEditor) {
                    this.textEditor.editorContainer.alpha = 0;
                }
                // if (this.placeHolderComponent) {
                //     this.placeHolderComponent.alpha = 0;
                // }
                this.placeHolderComponent?.render(textureCanvas);
                this.editor3D?.editorContainer.render(textureCanvas);
            });
        }
        if (!this.children.includes(this.rendererComponent3D)) {
            this.addChildAt(this.rendererComponent3D, 0);
        }
        this.rendererComponent3D.render3D();
        if (this.children.includes(this.baseComponent)) {
            this.removeChild(this.baseComponent);
        }
        if (this.textEventRectComponent) {
            this.removeChild(this.textEventRectComponent);
        }
        if (!this.checkShouldRenderFlatText()) {
            // 如果文字随形状旋转，则不渲染平面文字
            return;
        }
        this.renderEditor();
    };

    // 插入|更新 基础图形部分
    private renderBase = () => {
        // 没有形状类型，不渲染图形部分
        if (!this.factoryOptions.model.props.style.bgShape) return;
        if (!this.baseComponent) {
            this.baseComponent = new ElementBaseComponent(this.factoryOptions);
        }
        if (!this.children.includes(this.baseComponent)) {
            this.addChildAt(this.baseComponent, 0);
        }
        if (this.rendererComponent3D && this.children.includes(this.rendererComponent3D)) {
            // 平面形状和 3d 形状渲染互斥
            this.removeChild(this.rendererComponent3D);
        }
        this.baseComponent.renderComponent();
    };

    // 插入|更新 文本框影响区域（文本框设置不自动调整尺寸时，editor不能充满shape，需要加填充来响应事件）
    private renderTextEventRect = () => {
        if (this.checkShouldRender3D()) {
            // 3d 不需要此交互补足，且渲染后会影响 3d 形状事件响应，故屏蔽渲染
            return;
        }
        const element = this.factoryOptions.model;
        if (!element.isTextOrPlaceholder || (!this.factoryOptions.isEditable && element.isPlaceholder)) {
            return;
        }
        if (!this.textEventRectComponent) {
            this.textEventRectComponent = new TextEventRectComponent(this.factoryOptions, () => {
                if (this.logic?.ifCanSetReadyEditing?.()) {
                    this.textEditor?.setReadyEditing();
                }
            });
            this.addChildAt(this.textEventRectComponent, 0);
        }
        this.textEventRectComponent.renderComponent();
    };

    // 插入|更新 母版占位符
    private renderPlaceHolder = (): void => {
        if (!this.isPlaceHolderShow) {
            if (this.placeHolderComponent) {
                this.placeHolderComponent.dispose();
                this.removeChild(this.placeHolderComponent);
                this.placeHolderComponent = null;
            }
            // 渲染文本框的占位符边框
            this.renderPlaceholderBorder();
            return;
        }
        const { isPreview, region, isLeftScale, model } = this.factoryOptions;
        const isTemplate = region === RenderPostionType.TEMPLATE;
        if (!this.placeHolderComponent) {
            this.placeHolderComponent = new PlaceHolderComponent(
                this.factoryOptions,
                isTemplate,
                !!isLeftScale,
                isPreview,
            );
            this.placeHolderComponent.registerPlaceHolderClick(this.logic?.handlePlaceHolderClick);
            this.addChild(this.placeHolderComponent);
        } else {
            this.placeHolderComponent.registerPlaceHolderClick(this.logic?.handlePlaceHolderClick);
            this.placeHolderComponent.updateComponent(isPreview, isLeftScale);
        }
        if (!this.factoryOptions.fromScale) {
            // 和注册逻辑对应
            const curEditor = this.placeHolderComponent.getEditor();
            if (curEditor && model.editorInstance) {
                // 从editor变更为placeHolder,变更样式
                model.selectionStyles = curEditor.getAllStylesSync();
            }
            model.setEditorComponent(curEditor);
        }
        this.removePlaceholderBorder();
    };

    // 插入|更新 链接标识小图标（右键编辑链接生成）
    private renderShapeLinkFlag = () => {
        if (!this.shapeLinkFlagComponent) {
            this.shapeLinkFlagComponent = new ShapeLinkFlagComponent(this.factoryOptions, this);
        }
        this.shapeLinkFlagComponent.renderComponent();
    };

    /**
     * 是否需要render内容。
     */
    private isShouldRender() {
        // 部分ph做不显示处理
        return !getPlaceHolderUnsupport(this.factoryOptions.model);
    }

    private get isPlaceHolderShow() {
        const { model, region } = this.factoryOptions;
        // 是否是通过ph插入的element
        const isCustomPlaceholder = getIsCustomPlaceholder(model);
        // 用户是否修改过ph的默认显示文案
        const hasCustomPrompt = getHasCustomPrompt(model);

        // 是否显示ph
        return isPlaceHolderShow(
            isCustomPlaceholder,
            this.logic?.isEditorFocus || this.textEditor?.isEditing,
            model,
            hasCustomPrompt,
            model.isPlaceholder,
            region,
        );
    }

    private shouldShowEditor() {
        const { isEditable, model } = this.factoryOptions;
        const isDeadEditor = !isEditable && isEditorEmpty(model.paragraphList);
        return !isDeadEditor && (!this.isPlaceHolderShow || this.showTempTextEditor) && !isIframeShapeModel(model);
    }

    // 插入|更新 文本框
    // eslint-disable-next-line complexity
    private renderTextEditor = (): void => {
        if (!this.shouldShowEditor()) {
            if (this.textEditor) {
                this.textEditor.editorContainer.dispose();
                this.removeChild(this.textEditor.editorContainer);
                this.textEditor = null;
            }
            return;
        }
        const { model, fromScale } = this.factoryOptions;
        if (model.isPlaceholder) {
            savePlaceHolderParaEndProps(model);
        }
        this.logic?.updateResizevalue?.();
        const readProps = this.getEditorReadOnlyProps();
        const editorProps: IEditorProps = this.logic?.getEditorProps
            ? { ...readProps, ...this.logic.getEditorProps() }
            : readProps;

        this.wordArtComponent?.renderComponentPreprocess({ editorProps });

        if (this.textEditor) {
            this.textEditor.updateEditorProps(editorProps);
            if (!this.checkShouldRender3D() || this.checkShouldRenderFlatText()) {
                this.textEditor.editorContainer.alpha = 1;
            }
        } else {
            this.textEditor = new TextEditor(editorProps, { ...this.factoryOptions });
            if (this.textContainer) {
                this.textContainer.addChild(this.textEditor.editorContainer);
            } else {
                this.addChild(this.textEditor.editorContainer);
            }
        }
        this.renderEditor3D(editorProps);
        if (!fromScale && !this.isPlaceHolderShow) {
            model.setEditorComponent(this.textEditor);
        }
        // 如果图形翻转，则对文本框进行逆向翻转，保证其正常的阅读体验
        reverseComponentFlip(this.textEditor.editorContainer, this.factoryOptions, true);
    };

    // 插入|更新 文本容器
    private renderTextContainer() {
        if (!this.shouldShowTextContainer()) {
            if (this.textContainer) {
                this.textContainer.dispose();
                this.removeChild(this.textContainer);
                this.textContainer = null;
            }
            return;
        }
        if (!this.textContainer) {
            this.textContainer = new Container();
            this.addChild(this.textContainer);
        }
        const { model, frame } = this.factoryOptions;
        const textFrame = model.props.textFrame!;
        const size = getSize(frame, model.props);
        /**
         * 目的：textFrame 是相对于父级元素的，并不是相对于当前形状，而 textContainer 是当前 shape 的子元素，所以 textContainer 的位置需要改为相对于当前 shape 的
         * 但是目前并没有摸清楚 textFrame 的还原逻辑，只是在一些特殊场景下能差不多还原
         * 这里是文字和形状都旋转 180° 时（这个场景相对多一点）的特殊处理逻辑
         */
        if (textFrame.rotate && Math.abs(textFrame.rotate) === 180 && frame?.rotate && Math.abs(frame.rotate) === 180) {
            // 计算文本区域旋转前的起始点
            const [textStartBeforeRotate] = getPointsAfterRotate(textFrame, -textFrame.rotate);
            // 计算形状区域旋转后的起始点
            const [shapeStartAfterRotate] = getPointsAfterRotate(size, frame.rotate);
            updateContainerTransform(this.textContainer, {
                ...textFrame,
                left: Math.abs(shapeStartAfterRotate.x - textStartBeforeRotate.x),
                top: Math.abs(shapeStartAfterRotate.y - textStartBeforeRotate.y),
            });
            return;
        }
        // 其他情况下都按照没旋转的逻辑计算
        updateContainerTransform(this.textContainer, {
            ...textFrame,
            left: textFrame.left - size.left,
            top: textFrame.top - size.top,
        });
    }

    // 预渲染艺术字组件
    private preRenderWordArt = () => {
        if (!checkShouldRenderWordArt(this.factoryOptions)) return;
        if (!this.wordArtComponent) {
            this.wordArtComponent = new WordArtComponent(this.factoryOptions, this);
        }
    };

    // 渲染艺术字组件
    private renderWordArt = () => {
        this.wordArtComponent?.renderComponent();
    };

    private shouldShowTextContainer() {
        return !!this.factoryOptions.model.props.textFrame && this.shouldShowEditor();
    }

    // 插入|更新 图片前景，场景为 iframe 需要底图供切换到 dom
    private renderForeground = () => {
        if (!this.foregroundComponent) {
            this.foregroundComponent = new ForegroundComponent(this.factoryOptions);
            this.addChild(this.foregroundComponent);
        }
        this.foregroundComponent.renderComponent();
    };

    // 添加 iframe 底图播放图标
    private renderPlayerIcon = () => {
        if (!this.iframeIconComponent) {
            this.iframeIconComponent = new IframeIconComponent(this.factoryOptions, this);
        }
        this.iframeIconComponent.updateComponent();
    };

    /**
     * 获取editor查看态props
     */
    private getEditorReadOnlyProps = (): IEditorProps => {
        const { model, isEditable, fromScale, region, modelParams, anim } = this.factoryOptions;
        // 缩略图默认更新，编辑区根据changeSet判断是否需要更新
        const shouldUpdate = !fromScale ? modelParams?.updateState?.editorShouldUpdate : true;
        const slideModel = model.slideRef || this.factoryOptions.slideModel;
        return {
            paragraphList: this.getParagraphList(),
            textBodyStyle: this.getTextBodyStyle(),
            model,
            modelKey: model.key,
            modelKeySuffix: `-${this.factoryOptions.region}`,
            shouldUpdate: (!!anim?.needUpdateFont) || shouldUpdate,
            textRect: this.getTextRect(),
            // 母版编辑情况，幻灯片编号设置为不可能存在的值，退出母版编辑后才可以正常更新
            slideNum: this.factoryOptions.isMasterEdit ? -1 : getSlideIndex(slideModel) + 1,
            datetime: this.factoryOptions.infrastructureService.initDatetime,
            isTitle: isTitleElement(model),
            editable: isEditable,
            selectable:
                !fromScale
                && ((model.slideRef && !this.factoryOptions.isMasterEdit) || this.factoryOptions.isMasterEdit),
            selected: model.selected,
            linkCursor: region === RenderPostionType.PLAYER ? 'pointer' : '',
            linkColor: this.getLinkThemeColor(),
            getRenderTextExtraOption: this.getRenderTextExtraOption,
            getApplication: this.factoryOptions.getApplication?.bind(this),
            cursor: this.getInitialCursor(),
            resize: this.logic?.setResize,
            setResize: this.logic?.setResizeValue,
            // 和 office 保持一致，title 和 ctrTitle 不支持设置缩进
            disableIndentation: model.isTitleOrCtrTitlePl,
        };
    };

    private getParagraphList = (): IParagraph[] | undefined => {
        const { model, anim } = this.factoryOptions;
        if (!model.paragraphList || !anim?.needUpdateFont) return model.paragraphList;
        if (!anim?.fontColor && !anim?.fontSize) return model.paragraphList;
        const styles = this.getTextRunStyles(anim);
        const newParagraphList = setTextRunStyles(model.paragraphList, styles);
        return newParagraphList;
    };

    private getTextBodyStyle = (): ITextBodyStyle => {
        const { model } = this.factoryOptions;
        return model.textBodyStyle;
    };

    /**
     * 获取动画运行阶段需要更新的字体属性
     * @param anim IAnimOption
     * @returns Partial<ITextRunStyle>
     */
    private getTextRunStyles(anim: IAnimOption): Partial<ITextRunStyle> {
        const handlerMap = {
            fontColor: () => ({ tdColor: new TDColor({ rgbaStr: anim.fontColor }) }),
            fontSize: () => ({ fontSize: anim.fontSize }),
        };

        const styleObj = Object.entries(handlerMap).reduce((acc, [key, handler]) => {
            const animValue = anim[key];
            if (!animValue || !handler) return acc;
            return {
                ...acc,
                ...handler(),
            };
        }, {});

        return styleObj;
    }

    private getLinkThemeColor = () => {
        const { model } = this.factoryOptions;
        const slideModel = model.slideRef || this.factoryOptions.slideModel;
        return this.factoryOptions.linkManager?.linkUtils.getLinkColor(slideModel);
    };

    // todo 等logic重构后，这部分修改移除。需要加这个是因为目前readyOnlyProps没办法拿到logic
    // 而update不应该更新cursor(因为cursor会随着state更改，如果Update里setCursor，会用props的初始值覆盖当前值)
    private getInitialCursor() {
        const { region, model } = this.factoryOptions;
        if (region === RenderPostionType.PLAYER) {
            return checkClickElement(this.factoryOptions) ? 'pointer' : 'auto';
        }

        if (model.parent?.type === UIModelType.SMART_ART) return 'auto';
        const { uniqueID } = model || {};

        // 如果实例化的时候，格式刷处于激活状态，需要设置为格式化光标
        const { formatBrushManager } = this.factoryOptions;
        const animBrushManager = (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            ?.editableServiceLayer?.animBrushManager;

        if (needBrushCursor({ formatBrushManager, animBrushManager })) {
            const animBrushState = animBrushManager?.animBrushState;
            const formatBrushState = formatBrushManager?.formatBrushState;

            if (uniqueID !== animBrushState?.originElId) {
                return getBrushCursorStyle(formatBrushState?.data === FORMAT_BRUSH_TEXT);
            }
        }

        return 'text';
    }

    private getRenderTextExtraOption = (option) => {
        const editorDirection = this.factoryOptions.model.editorInstance?.editorModel?.textDirection;
        const modelDirection = this.factoryOptions.model.textBodyStyle?.wrapperStyle?.textDirection;
        return {
            forbidWebgl: FORBID_WEBGL_FILTER,
            effectScale: 1 / CANVAS_PT_SCALE,
            shouldShowTextFill: isShowTextFill(option.textElement),
            // 当切换横排竖排时model上的textDirection尚未变更，需要读取editorInstance上的
            textDirection: editorDirection ?? modelDirection ?? TextDirectionType.HORIZONTAL,
            filterDisplayOptions: this.factoryOptions.getFilterDisplayOptions?.(),
            // 生成图片场景 && 图形大小超过阈值则禁掉滤镜
            forbidFilter: isCopyToImageOverSize(
                this.factoryOptions.exportImageManager?.isImageCopying,
                option?.logicWidth,
                option?.logicHeight,
            ),
        };
    };

    private getTextRect = (): ITextRect => {
        const { model, frame } = this.factoryOptions;
        const { textFrame } = model.props;
        if (textFrame) {
            const { width, height } = textFrame;
            return {
                left: 0,
                top: 0,
                width,
                height,
            };
        }
        const size = getSize(frame, model.props);
        return getTextRect(model, model.pathParser, size);
    };

    private getAddonManager(): IAddonManager | undefined {
        return this.factoryOptions.addonManager;
    }

    /**
     * 渲染占位符边框和图标
     */
    private renderPlaceholderBorder = (): void => {
        const { isFromSlide, isPlaceholder } = this.factoryOptions.model;
        // 页编辑模式下或者非占位符不显示占位符边框
        if (isFromSlide || !isPlaceholder) {
            return;
        }
        if (!this.placeHolderGraphics) {
            this.placeHolderGraphics = new PlaceholderElement(this.factoryOptions);
            this.addChild(this.placeHolderGraphics);
        } else {
            this.placeHolderGraphics.updatePlaceholder();
        }
    };

    private removePlaceholderBorder = (): void => {
        if (this.placeHolderGraphics) {
            this.placeHolderGraphics.dispose();
            this.removeChild(this.placeHolderGraphics);
            this.placeHolderGraphics = null;
        }
    };

    private handleAddonInfoListLoaded = () => {
        this.renderPlayerIcon();
        const { model } = this.factoryOptions;
        const iframeId = model.props.addon?.iframeId ?? '';
        const addonManager = this.getAddonManager();
        if (!addonManager) return;
        const isOfficial = addonManager?.addonHandler.addonInfoMap.get(iframeId)?.isOfficial;
        if (!isOfficial && !(AddonDoNotAddIconList.indexOf(iframeId) > -1)) {
            this.factoryOptions.mediaManager?.iframePosterCaches.set(model.key, addonDefaultForeground);
            this.renderForeground();
        }
    };

    private checkShouldRenderFlatText(): boolean {
        return !!this.factoryOptions.model.textBodyStyle?.bodyStyle?.isFlatText;
    }

    private checkShouldRender3D(): boolean {
        return (
            shouldRender3D()
            && this.shouldRender3D
            && is3DModel(this.factoryOptions.model)
            && !this.isPlaceHolderShow
            && !this.factoryOptions.fromScale
        ); // todo: 临时解决加载千页ppt内存过大问题：从缩略图进入时，不渲染3D
    }

    private renderEditor3D(editorProps: IEditorProps) {
        if (this.checkShouldRender3D()) {
            if (this.editor3D) {
                this.editor3D.updateEditorProps(editorProps);
            } else {
                this.editor3D = new TextEditor(editorProps, { ...this.factoryOptions });
            }
        } else {
            this.dispose3D();
        }
    }
    private observeAnimBrushManager() {
        if (this.animBrushDisposer) return;
        const animBrushManager = (AppServiceProvider.getInstance()?.getAppServices() as IEditorAppServices)
            .editableServiceLayer?.animBrushManager;
        if (!animBrushManager) return;
        this.animBrushDisposer = reaction(() => {
            const animBrushState = (AppServiceProvider.getInstance()?.getAppServices() as IEditorAppServices)
                .editableServiceLayer?.animBrushManager.animBrushState;
            return [animBrushState];
        }, () => {
            const cursor = this.getInitialCursor();
            this.updateTextEditorCursor(cursor);
        });
    }

    private dispose3D() {
        if (this.editor3D) {
            this.editor3D.dispose();
            this.editor3D = null;
        }
    }
}
